import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Eye, Edit, Trash2, BookOpen, MapPin, GraduationCap, DollarSign } from 'lucide-react';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import apiService from '../../services/api';

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: 'undergraduate',
    duration: '',
    fees: '',
    currency: 'USD',
    description: '',
    requirements: '',
    country_id: '',
    university_id: '',
    status: 'active'
  });

  const categories = [
    { value: 'undergraduate', label: 'Undergraduate' },
    { value: 'postgraduate', label: 'Postgraduate' },
    { value: 'diploma', label: 'Diploma' },
    { value: 'certificate', label: 'Certificate' }
  ];

  useEffect(() => {
    fetchCourses();
    fetchCountries();
    fetchUniversities();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCourses({ limit: 100 });
      setCourses(response.data);
    } catch (err) {
      toast.error('Failed to fetch courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await apiService.getCountries({ status: 'active', limit: 100 });
      setCountries(response.data);
    } catch (err) {
      console.error('Failed to fetch countries:', err);
    }
  };

  const fetchUniversities = async () => {
    try {
      const response = await apiService.getUniversities({ status: 'active', limit: 100 });
      setUniversities(response.data);
    } catch (err) {
      console.error('Failed to fetch universities:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const courseData = {
        ...formData,
        fees: formData.fees ? parseFloat(formData.fees) : null,
        country_id: formData.country_id || null,
        university_id: formData.university_id || null
      };

      if (editingCourse) {
        await apiService.updateCourse(editingCourse.id, courseData);
      } else {
        await apiService.createCourse(courseData);
      }

      toast.success(`Course ${editingCourse ? 'updated' : 'created'} successfully`);
      setShowModal(false);
      setEditingCourse(null);
      resetForm();
      fetchCourses();
    } catch (err) {
      toast.error(err.message || 'Operation failed');
    }
  };

  const handleView = (course) => {
    setSelectedCourse(course);
    setShowViewModal(true);
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name,
      code: course.code || '',
      category: course.category,
      duration: course.duration || '',
      fees: course.fees || '',
      currency: course.currency || 'USD',
      description: course.description || '',
      requirements: course.requirements || '',
      country_id: course.country_id || '',
      university_id: course.university_id || '',
      status: course.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        await apiService.deleteCourse(id);
        toast.success('Course deleted successfully');
        fetchCourses();
      } catch (err) {
        toast.error(err.message || 'Failed to delete course');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      category: 'undergraduate',
      duration: '',
      fees: '',
      currency: 'USD',
      description: '',
      requirements: '',
      country_id: '',
      university_id: '',
      status: 'active'
    });
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingCourse(null);
    resetForm();
  };

  const handleAddCourse = () => {
    setEditingCourse(null);
    resetForm();
    setShowModal(true);
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'code', label: 'Code' },
    { key: 'category', label: 'Category' },
    { key: 'duration', label: 'Duration' },
    {
      key: 'fees',
      label: 'Fees',
      render: (course) => course.fees ? `${course.currency} ${course.fees}` : '-'
    },
    {
      key: 'country',
      label: 'Country',
      render: (course) => course.country?.name || '-'
    },
    {
      key: 'university',
      label: 'University',
      render: (course) => course.university?.name || '-'
    },
    { key: 'status', label: 'Status' },
    {
      key: 'actions',
      label: 'Actions',
      render: (course) => (
        <div className="action-buttons">
          <motion.button
            className="btn-sm touch-target"
            onClick={() => handleView(course)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="View Details"
          >
            <Eye size={12} />
          </motion.button>
          <motion.button
            className="btn-sm warning touch-target"
            onClick={() => handleEdit(course)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Edit"
          >
            <Edit size={12} />
          </motion.button>
          <motion.button
            className="btn-sm danger touch-target"
            onClick={() => handleDelete(course.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Delete"
          >
            <Trash2 size={12} />
          </motion.button>
        </div>
      )
    }
  ];

  return (
    <div className="courses-management">
      <div className="page-header">
        <h2><BookOpen size={24} /> Courses Management</h2>
        <motion.button
          className="btn-primary"
          onClick={handleAddCourse}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={16} /> Add Course
        </motion.button>
      </div>

      <DataTable
        data={courses}
        columns={columns}
        loading={loading}
        emptyMessage="No courses found"
      />

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Course Details"
      >
        {selectedCourse && (
          <div style={{ textAlign: 'left', padding: '0' }}>
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '2rem',
              borderRadius: '12px 12px 0 0',
              marginBottom: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <BookOpen size={40} />
              </div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>{selectedCourse.name}</h3>
              <p style={{ margin: '0', opacity: '0.9' }}>{selectedCourse.category}</p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Information</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {selectedCourse.code && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                    <BookOpen size={18} style={{ color: '#6b7280' }} />
                    <span style={{ color: '#374151', fontWeight: '500' }}>Code:</span>
                    <span style={{ color: '#374151' }}>{selectedCourse.code}</span>
                  </div>
                )}
                {selectedCourse.duration && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                    <GraduationCap size={18} style={{ color: '#6b7280' }} />
                    <span style={{ color: '#374151', fontWeight: '500' }}>Duration:</span>
                    <span style={{ color: '#374151' }}>{selectedCourse.duration}</span>
                  </div>
                )}
                {selectedCourse.fees && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                    <DollarSign size={18} style={{ color: '#6b7280' }} />
                    <span style={{ color: '#374151', fontWeight: '500' }}>Fees:</span>
                    <span style={{ color: '#374151' }}>{selectedCourse.currency} {selectedCourse.fees}</span>
                  </div>
                )}
                {selectedCourse.country && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                    <MapPin size={18} style={{ color: '#6b7280' }} />
                    <span style={{ color: '#374151', fontWeight: '500' }}>Country:</span>
                    <span style={{ color: '#374151' }}>{selectedCourse.country.name}</span>
                  </div>
                )}
                {selectedCourse.university && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                    <GraduationCap size={18} style={{ color: '#6b7280' }} />
                    <span style={{ color: '#374151', fontWeight: '500' }}>University:</span>
                    <span style={{ color: '#374151' }}>{selectedCourse.university.name}</span>
                  </div>
                )}
              </div>
            </div>

            {selectedCourse.description && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Description</h4>
                <p style={{ color: '#6b7280', lineHeight: '1.6', margin: '0', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  {selectedCourse.description}
                </p>
              </div>
            )}

            {selectedCourse.requirements && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Requirements</h4>
                <p style={{ color: '#6b7280', lineHeight: '1.6', margin: '0', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  {selectedCourse.requirements}
                </p>
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: selectedCourse.status === 'active' ? '#dcfce7' : '#fef3c7',
                color: selectedCourse.status === 'active' ? '#166534' : '#92400e',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: selectedCourse.status === 'active' ? '#22c55e' : '#f59e0b',
                  borderRadius: '50%'
                }} />
                {selectedCourse.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        )}
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleModalClose}
        title={`${editingCourse ? 'Edit' : 'Add'} Course`}
      >
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Code</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Category *</label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Duration</label>
              <input
                type="text"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                placeholder="e.g., 4 years, 2 semesters"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Fees</label>
              <input
                type="number"
                step="0.01"
                value={formData.fees}
                onChange={(e) => setFormData({ ...formData, fees: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
                <option value="AUD">AUD</option>
                <option value="INR">INR</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Country</label>
              <select
                value={formData.country_id}
                onChange={(e) => setFormData({ ...formData, country_id: e.target.value })}
              >
                <option value="">Select Country</option>
                {countries && countries.map(country => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>University</label>
              <select
                value={formData.university_id}
                onChange={(e) => setFormData({ ...formData, university_id: e.target.value })}
              >
                <option value="">Select University</option>
                {universities && universities.map(university => (
                  <option key={university.id} value={university.id}>{university.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Requirements</label>
            <textarea
              value={formData.requirements}
              onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
              rows="3"
              placeholder="Entry requirements, prerequisites, etc."
            />
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleModalClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingCourse ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Courses;